import { useMemo } from 'react';
import { Check } from 'lucide-react';

import { useGetFamilyStudents } from '~entities/student/api/get-family-students';
import { formatDateOnlySafe } from '~shared/helpers/datetime';
import { Button } from '~shared/shadcn/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '~/shared/shadcn/ui/card';

interface FamilyStudentsSelectorProps {
  parentId: string;
  studioId: string;
  selectedStudentId?: string;
  onSelectStudent: (studentId: string) => void;
  onAddNew: () => void;
}

export function FamilyStudentsSelector({
  parentId,
  studioId,
  selectedStudentId,
  onSelectStudent,
  onAddNew,
}: FamilyStudentsSelectorProps) {
  const { data: familyStudents, isLoading } = useGetFamilyStudents(parentId);

  const hasStudents = useMemo(
    () => familyStudents && familyStudents.length > 0,
    [familyStudents],
  );

  if (isLoading) {
    return (
      <Card>
        <CardContent className='pt-6'>
          <p className='text-sm text-muted-foreground'>Loading...</p>
        </CardContent>
      </Card>
    );
  }

  if (!hasStudents) {
    return null;
  }

  return (
    <Card className='mb-4'>
      <CardHeader>
        <CardTitle>Existing Family Members</CardTitle>
        <CardDescription>
          Select an existing student or add a new one
        </CardDescription>
      </CardHeader>
      <CardContent className='space-y-2'>
        {familyStudents?.map((student) => (
          <Button
            key={student._id}
            type='button'
            variant={
              selectedStudentId === student._id ? 'default' : 'outline'
            }
            className='w-full justify-start'
            onClick={() => onSelectStudent(student._id)}
          >
            {selectedStudentId === student._id && (
              <Check className='mr-2 h-4 w-4' />
            )}
            {student.firstName} {student.lastName}
            {student.dob &&
              ` (${formatDateOnlySafe(student.dob, 'M/d/yyyy')})`}
          </Button>
        ))}
        <Button
          type='button'
          variant='outline'
          className='w-full'
          onClick={onAddNew}
        >
          + Add New Student
        </Button>
      </CardContent>
    </Card>
  );
}

